<template>
  <div class="flex min-h-screen font-sans">
    <!-- Section gauche - Branding -->
    <div class="flex-1 bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center p-10 relative overflow-hidden">
      <!-- Decorative circles -->
      <div class="absolute w-[500px] h-[500px] bg-secondary/10 rounded-full -top-24 -left-24"></div>
      <div class="absolute w-[400px] h-[400px] bg-secondary/5 rounded-full -bottom-24 -right-24"></div>

      <div class="relative z-10 text-center text-white">
        <div class="mb-8">
          <i class="fa-solid fa-screwdriver-wrench text-8xl mb-6 opacity-90 animate-pulse"></i>
        </div>
        <h2 class="text-4xl font-bold mb-4 leading-tight">
          Maintenance en cours
        </h2>
        <p class="text-lg opacity-90 font-light">
          Nous travaillons pour améliorer votre expérience
        </p>
      </div>
    </div>

    <!-- Section droite - Contenu -->
    <div class="flex-1 flex items-center justify-center p-10 bg-white">
      <div class="w-full max-w-[440px]">
        <!-- Header -->
        <div class="mb-10">
          <h1 class="text-primary text-3xl font-bold mb-2">
            Système en maintenance
          </h1>
          <p class="text-gray-500 text-base">
            Le système est actuellement en maintenance. Veuillez réessayer dans quelques instants.
          </p>
        </div>

        <!-- Status messages -->
        <div v-if="isChecking" class="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6 text-center">
          <div class="w-12 h-12 border-4 border-gray-200 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
          <p class="text-gray-600 text-sm">Vérification de la disponibilité du système...</p>
        </div>

        <div v-else-if="lastError" class="bg-red-50 text-red-900 px-4 py-3.5 rounded-lg mb-6 text-sm border-l-4 border-red-500">
          {{ lastError }}
        </div>

        <!-- Refresh Button -->
        <BaseButton 
          variant="primary" 
          size="lg" 
          block
          @click="checkSystemStatus"
          :loading="isChecking"
          class="hover:-translate-y-0.5 active:translate-y-0 transition-transform"
        >
          <template #icon-left>
            <i class="fa-solid fa-arrow-rotate-right"></i>
          </template>
          {{ isChecking ? 'Vérification...' : 'Actualiser' }}
        </BaseButton>

        <!-- Footer -->
        <footer class="text-center text-gray-400 text-xs pt-6 mt-8 border-t border-gray-200">
          <p class="mb-2">
            © {{ new Date().getFullYear() }} SellHub. Tous droits réservés.
          </p>
          <div class="flex items-center justify-center text-xs">
            <span>Powered by </span>
            <a
              href="https://digita.sn"
              target="_blank"
              class="text-secondary font-semibold ml-1 hover:text-primary transition-colors"
            >
              Digita SN
            </a>
          </div>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { authService } from '@/services'
import ApiService from '@/services/apiService'
import BaseButton from '@/components/base/BaseButton.vue'

export default defineComponent({
  name: 'MaintenanceView',
  components: {
    BaseButton
  },
  setup() {
    const router = useRouter()
    const isChecking = ref(false)
    const lastError = ref(null)

    const checkSystemStatus = async () => {
      isChecking.value = true
      lastError.value = null

      try {
        // Forcer un appel API réel pour vérifier si le système est disponible
        // On utilise directement ApiService.get pour éviter le cache du localStorage
        await ApiService.get('/auth/me')
        
        // Si on arrive ici, le système est de nouveau disponible
        // Désactiver le mode maintenance
        sessionStorage.removeItem('maintenance_mode')
        
        // Recharger les données utilisateur
        await authService.getUser()
        
        // Rediriger vers la page d'accueil
        router.push({ name: 'dashboard' })
      } catch (error) {
        console.error('Erreur lors de la vérification:', error)
        
        // Si c'est toujours un 503, on reste sur la page de maintenance
        if (error.response && error.response.status === 503) {
          lastError.value = 'Le système est toujours en maintenance'
          // S'assurer que le mode maintenance est activé
          sessionStorage.setItem('maintenance_mode', 'true')
        } else if (error.response) {
          lastError.value = error.response.data?.message || 'Erreur lors de la vérification'
        } else {
          lastError.value = 'Impossible de se connecter au serveur'
        }
      } finally {
        isChecking.value = false
      }
    }

    // Vérifier automatiquement au montage
    onMounted(() => {
      checkSystemStatus()
    })

    return {
      isChecking,
      lastError,
      checkSystemStatus
    }
  }
})
</script>

<style scoped>
/* Responsive design for smaller screens */
@media (max-width: 968px) {
  .flex {
    flex-direction: column;
  }
}
</style>
